import { AlertCircle, Loader2, XCircle } from "lucide-react";
import React, { FormEvent } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Textarea from "../ui/Textarea";

interface RejectPostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  postId: number | null;
  rejectReason: string;
  setRejectReason: (reason: string) => void;
}

const RejectPostModal: React.FC<RejectPostModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  postId,
  rejectReason,
  setRejectReason,
}) => {
  const { t } = useTranslation();
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onConfirm();
  };

  const characterCount = rejectReason.length;
  const minCharacters = 10;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Rejeitar Post")}
      maxWidth="max-w-lg"
      footer={
        <div className="flex flex-row justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            variant="destructive"
            size="sm"
            icon={
              loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )
            }
            disabled={
              loading ||
              !rejectReason.trim() ||
              rejectReason.trim().length < minCharacters
            }
            form="reject-post-form"
          >
            {loading ? t("Rejeitando...") : t("Confirmar Rejeição")}
          </Button>
        </div>
      }
    >
      <form id="reject-post-form" onSubmit={handleSubmit} className="py-2">
        <div className="flex items-start gap-4 mb-4">
          <div
            className={cn(
              "shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
              "bg-amber-100 text-amber-600",
            )}
          >
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t("Motivo da Rejeição")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("Informe o motivo da rejeição do post")}{" "}
              <span className="font-semibold text-gray-900">#{postId}</span>.{" "}
              {t("Este feedback ajudará a melhorar futuros posts.")}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t(
              "Ex: Conteúdo não está alinhado com nossa marca, precisa de mais informações técnicas, formato inadequado...",
            )}
            required
            inputSize="md"
            rows={4}
            className="resize-y"
            error={
              rejectReason.length > 0 &&
              rejectReason.trim().length < minCharacters
                ? t("O motivo deve ter pelo menos {min} caracteres", {
                    min: minCharacters,
                  })
                : undefined
            }
          />
          <div className="flex items-center justify-between text-xs">
            <span
              className={cn(
                "text-gray-500",
                rejectReason.trim().length < minCharacters &&
                  rejectReason.length > 0 &&
                  "text-amber-600",
              )}
            >
              {rejectReason.trim().length < minCharacters
                ? t("Mínimo de {min} caracteres ({count}/{min})", {
                    min: minCharacters,
                    count: rejectReason.trim().length,
                  })
                : t("{count} caracteres", { count: characterCount })}
            </span>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default RejectPostModal;
